import { useRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { fadeInImageAnimation } from 'styles/theme/animation';
import { useWindowSize } from 'shared/hooks/useWindowSize';

interface IArticleFadeInImage {
  children: React.ReactNode;
  delayAnimation?: boolean;
  triggerDistance?: number;
}

/*
[ArticleFadeInImage]  An image component that moves upward + fades into existence
as it comes into the viewport (by scrolling downward to its position).
*/

export const ArticleFadeInImage = ({
  children,
  delayAnimation,
  triggerDistance = 0,
}: IArticleFadeInImage) => {
  const { isMobile } = useWindowSize();
  const imageRef = useRef<HTMLInputElement>(null);
  const [isVisible, setIsVisible] = useState(isMobile);

  useEffect(() => {
    const checkVisibility = () => {
      if (!imageRef?.current || isVisible) return;

      const bound = imageRef.current.getBoundingClientRect();
      const td = triggerDistance;
      if (bound?.top <= window.innerHeight + td) {
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', checkVisibility);
    window.addEventListener('resize', checkVisibility);

    return () => {
      window.removeEventListener('scroll', checkVisibility);
      window.removeEventListener('resize', checkVisibility);
    };
  }, []);

  return (
    <ArticleFadeInImage.Wrapper
      $delayAnimation={delayAnimation}
      $isVisible={isVisible}
      ref={imageRef}
    >
      {children}
    </ArticleFadeInImage.Wrapper>
  );
};

ArticleFadeInImage.Wrapper = styled.div<{ $isVisible: boolean; $delayAnimation: boolean }>`
  display: block;
  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
  transform: translate3d(0, 250px, 0);
  backface-visibility: hidden;
  animation-delay: ${({ $delayAnimation }) => $delayAnimation && '0.3s'};

  ${({ $isVisible }) =>
    $isVisible &&
    css`
      ${fadeInImageAnimation()}
    `};
`;
